/**
 * 视频资源配置
 * 统一管理视频、音频等媒体资源的URL前缀
 * 
 * 注意：已移除域名判断逻辑，完全基于构建时的环境变量（MODE）来区分环境
 * 
 * 环境配置说明：
 * - 开发环境（development）：
 *   - API 接口：http://101.126.27.42:8000（vite.config.ts 默认值）
 *   - 可通过环境变量 VITE_API_URL 配置覆盖
 * - 正式环境（production）：
 *   - API 接口：http://101.126.159.198:46061（.env.production）
 *   - 资源前缀：https://prod-1321001571.tos-cn-guangzhou.volces.com（通过 Nginx 代理）

 */

/**
 * 基于构建时环境变量判断当前环境
 * 不再使用域名判断，完全依赖构建命令中的 --mode 参数
 */
export const getEnv = (): 'development' | 'testing' | 'production' => {
   // 通过 vite 的 import.meta.env.MODE 判断（构建时注入）
  try {
    // @ts-ignore - Vite 环境变量
    const mode = import.meta?.env?.MODE;
    if (mode) {
      // mode 可能是 'development' | 'test' | 'production'
      if (mode === 'production') {
        return 'production';
      }
      if (mode === 'test' || mode === 'testing') {
        return 'testing';
      }
      // development 或其他情况
      return 'development';
    }
  } catch (e) {
    // 忽略错误，继续其他判断方式
  }
  
  // 默认根据 NODE_ENV 判断（Node.js 环境，如 SSR）
  // @ts-ignore - process 在浏览器环境中可能不存在
  if (typeof process !== 'undefined' && process?.env?.NODE_ENV) {
    // @ts-ignore
    return process.env.NODE_ENV === 'production' ? 'production' : 'development';
  }
  
  // 默认返回 development
  return 'development';
};

/**
 * 视频资源URL前缀配置
 * 
 * 资源分类：
 * 1. /media - 内容哈希命名的资源（如 trans_audio），可以使用强缓存
 * 2. /storage - 其他资源（视频、字幕等），不使用强缓存
 * 
 * 代理说明：
 * - 开发环境：Vite 代理到火山对象存储
 * - 生产环境：Nginx 代理到火山对象存储（/media 设置强缓存，/storage 不设置强缓存）
 */
export const VIDEO_CDN_CONFIG = {
  // 开发环境前缀（内容哈希资源，可强缓存）
  DEVELOPMENT: '/media',
  // 测试环境前缀（内容哈希资源，可强缓存）
  TESTING: '/media',
  // 正式环境前缀（内容哈希资源，可强缓存）
  PRODUCTION: '/media',
};

/**
 * 非缓存资源URL前缀配置
 * 用于视频、字幕等可能频繁更新的资源
 */
export const VIDEO_STORAGE_CONFIG = {
  // 开发环境前缀（非缓存资源）
  DEVELOPMENT: '/storage',
  // 测试环境前缀（非缓存资源）
  TESTING: '/storage',
  // 正式环境前缀（非缓存资源）
  PRODUCTION: '/storage',
};

/**
 * 获取当前环境的视频资源前缀（内容哈希资源，可强缓存）
 * @returns 视频资源URL前缀
 */
export const getVideoCDNPrefix = (): string => {
  const env = getEnv();
  switch (env) {
    case 'production':
      return VIDEO_CDN_CONFIG.PRODUCTION;
    case 'testing':
      return VIDEO_CDN_CONFIG.TESTING;
    case 'development':
    default:
      return VIDEO_CDN_CONFIG.DEVELOPMENT;
  }
};

/**
 * 获取当前环境的存储资源前缀（非缓存资源）
 * 用于视频、字幕等可能频繁更新的资源
 * @returns 存储资源URL前缀
 */
export const getVideoStoragePrefix = (): string => {
  const env = getEnv();
  switch (env) {
    case 'production':
      return VIDEO_STORAGE_CONFIG.PRODUCTION;
    case 'testing':
      return VIDEO_STORAGE_CONFIG.TESTING;
    case 'development':
    default:
      return VIDEO_STORAGE_CONFIG.DEVELOPMENT;
  }
};

/**
 * 视频资源URL前缀（运行时动态获取，基于构建时的环境变量）
 * 使用对象包装，重写 valueOf 和 toString，这样在模板字符串中会自动调用
 */
class VideoCDNPrefix {
  valueOf(): string {
    return getVideoCDNPrefix();
  }
  
  toString(): string {
    return getVideoCDNPrefix();
  }
  
  // 支持 Symbol.toPrimitive (ES2015+)
  [Symbol.toPrimitive](hint: string): string {
    return getVideoCDNPrefix();
  }
}

/**
 * 视频资源URL前缀（导出为对象，运行时动态获取）
 * 在模板字符串中使用时会自动调用 valueOf/toString，返回正确的前缀
 */
export const VIDEO_CDN_PREFIX = new VideoCDNPrefix() as any as string;

